import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MAX_AUTOCOMPLETE_SELECT_OPTIONS,
  MIN_SEARCH_CHARACTERS,
} from 'src/config/app.config';
import { useApiError } from 'src/core/helper/use-api-error';
import { useGetLocationsPaginatedQuery } from 'src/core/service/location.service';
import { Autocomplete, AutocompleteProps } from 'src/shared/form/autocomplete';

import { Snackbar } from 'src/shared/snackbar/snackbar';
import { LocationModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/ocpi/src/protocol/v2.1.1/models/location.model';

type Props = Pick<
  AutocompleteProps<LocationModel>,
  'onChange' | 'label' | 'errorMessage' | 'isTouched'
> & {
  readonly cpoId?: string | null;
};

export function LocationSelect(props: Props): React.JSX.Element {
  const { t } = useTranslation();
  const { cpoId, onChange, ...autocompleteProps } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [searchedString, setSearchedString] = useState('');
  const [debouncedSearchedString, setDebouncedSearchedString] = useState('');
  const [hasSelectedOption, setHasSelectedOption] = useState(false);

  const DEBOUNCE_TIME = 400;

  // Trigger the API Call - need at least MIN_SEARCH_CHARACTERS
  // The searched string is debounced
  const {
    data: locationList,
    error,
    isError,
    isLoading,
  } = useGetLocationsPaginatedQuery(
    { cpoId: cpoId ?? '', name: `~${debouncedSearchedString}` },
    {
      skip:
        !isOpen ||
        !cpoId ||
        debouncedSearchedString.length < MIN_SEARCH_CHARACTERS,
    },
  );

  const totalResults = locationList?.totalDocs ?? 0;
  const { status, message } = useApiError(error);

  // Debounce the searched string
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchedString(searchedString);
    }, DEBOUNCE_TIME);
    return () => {
      clearTimeout(handler);
    };
  }, [searchedString]);

  useEffect(() => {
    setIsOpen(false);
    setSearchedString('');
    setDebouncedSearchedString('');
  }, [cpoId]);

  // Handle user choice of an option. Block input modification when a user make a choice.
  // User must clear the choice to be able to search again.
  function handleOnChange(value: LocationModel | null) {
    setHasSelectedOption(value !== null);
    if (value === null) {
      setSearchedString('');
      setDebouncedSearchedString('');
    }
    onChange(value);
    const inputElement = document.activeElement as HTMLInputElement;
    if (inputElement) {
      inputElement.blur();
    }
  }

  // Compute the 'no-option' text based on the autocomplete search string state.
  function getNoOptionsText() {
    if (debouncedSearchedString.length < MIN_SEARCH_CHARACTERS) {
      return t('model.location.noOptionsText.minSearchCharacters');
    }
    if (totalResults > MAX_AUTOCOMPLETE_SELECT_OPTIONS) {
      return t('model.location.noOptionsText.tooManyResults', {
        totalResults,
      });
    }
    return t('model.location.noOptionsText.default');
  }

  return (
    <>
      <Autocomplete<LocationModel>
        disabled={!cpoId}
        isInputReadOnly={hasSelectedOption}
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        inputValue={searchedString}
        onChange={handleOnChange}
        onInputChange={(newInputValue) => {
          if (newInputValue === null) {
            return;
          }
          if (newInputValue !== searchedString) {
            setSearchedString(newInputValue ?? '');
          }
        }}
        {...autocompleteProps}
        options={
          debouncedSearchedString.length >= MIN_SEARCH_CHARACTERS &&
          totalResults <= MAX_AUTOCOMPLETE_SELECT_OPTIONS
            ? locationList?.docs
            : []
        }
        isLoading={isLoading}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) =>
          t('model.location.result', {
            id: option.id,
            name: option.name,
          })
        }
        noOptionsText={getNoOptionsText()}
      />

      {isError && (
        <Snackbar
          variant="error"
          message={t('error.get-list-location-query', {
            status,
            message,
          })}
        />
      )}
    </>
  );
}
